/**
 * CurrencyHistoryTimeline — Vertical timeline of past currency conversions.
 *
 * Shows the user's history of currency changes (per CurrencyConversionEvent
 * model). Each entry shows from→to, timestamp, records affected, FX rate,
 * status badge, and a "Revert" button for completed events.
 *
 * Plan §24.7: the user-facing counterpart to the backend history API.
 */
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchEvents, rollbackEvent } from '@/features/currency/currencySlice';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { ArrowRight, CheckCircle2, AlertCircle, RotateCcw, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

function extractErrorMessage(payload: unknown): string {
  if (typeof payload === 'string' && payload) return payload;
  if (payload && typeof payload === 'object') {
    // Direct message/error fields
    const obj = payload as { message?: unknown; error?: unknown; reason?: unknown; data?: unknown; issues?: unknown; name?: unknown };
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (typeof obj.error === 'string' && obj.error) return obj.error;
    if (typeof obj.reason === 'string' && obj.reason) return obj.reason;
    // Nested error object: Hono envelope puts the ZodError at payload.error
    if (obj.error && typeof obj.error === 'object') {
      const nestedError = obj.error as { message?: unknown; name?: unknown; issues?: unknown };
      if (typeof nestedError.message === 'string' && nestedError.message) return nestedError.message;
      if (nestedError.name === 'ZodError' && Array.isArray(nestedError.issues) && nestedError.issues.length > 0) {
        const first = nestedError.issues[0] as { message?: unknown; path?: unknown };
        if (typeof first.message === 'string' && first.message) {
          const path = Array.isArray(first.path) && first.path.length > 0 ? first.path.join('.') + ': ' : '';
          return `${path}${first.message}`;
        }
      }
    }
    // Nested data.{message|error} shape (Axios + wrapped API envelope)
    if (obj.data && typeof obj.data === 'object') {
      const nested = obj.data as { message?: unknown; error?: unknown };
      if (typeof nested.message === 'string' && nested.message) return nested.message;
      if (typeof nested.error === 'string' && nested.error) return nested.error;
    }
    // ZodError shape: { name: 'ZodError', issues: [{ message, path, code }, ...] }
    if (obj.name === 'ZodError' && Array.isArray(obj.issues) && obj.issues.length > 0) {
      const first = obj.issues[0] as { message?: unknown; path?: unknown };
      if (typeof first.message === 'string' && first.message) {
        const path = Array.isArray(first.path) && first.path.length > 0 ? first.path.join('.') + ': ' : '';
        return `${path}${first.message}`;
      }
    }
  }
  return 'The server did not return a recognizable error. Please try again or contact support.';
}

interface RollbackTarget {
  eventId: string;
  fromCurrency: string;
  toCurrency: string;
}

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'text-yellow-600' },
  completed: { label: 'Completed', icon: CheckCircle2, color: 'text-green-600' },
  failed: { label: 'Failed', icon: AlertCircle, color: 'text-red-600' },
  rolled_back: { label: 'Rolled back', icon: RotateCcw, color: 'text-muted-foreground' },
} as const;

export function CurrencyHistoryTimeline() {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { events, eventsPagination, isLoading, error, rollbackInProgress } = useAppSelector(
    (state) => state.currency
  );

  const [rollbackTarget, setRollbackTarget] = useState<RollbackTarget | null>(null);
  const [rollbackReason, setRollbackReason] = useState('');

  useEffect(() => {
    dispatch(fetchEvents({ page: 1, limit: 20 }));
  }, [dispatch]);

  const handleLoadMore = () => {
    if (eventsPagination && eventsPagination.page < eventsPagination.totalPages) {
      dispatch(fetchEvents({ page: eventsPagination.page + 1, limit: 20 }));
    }
  };

  const handleRollback = async () => {
    if (!rollbackTarget || !rollbackReason.trim()) return;
    const idempotencyKey = `${rollbackTarget.eventId}-${crypto.randomUUID()}`;
    const result = await dispatch(rollbackEvent({
      eventId: rollbackTarget.eventId,
      idempotencyKey,
      reason: rollbackReason.trim(),
    }));
    if (rollbackEvent.fulfilled.match(result)) {
      toast({
        title: 'Conversion reverted',
        description: `Reverted ${rollbackTarget.fromCurrency} → ${rollbackTarget.toCurrency}.`,
      });
      setRollbackTarget(null);
      setRollbackReason('');
      // Refresh the list
      dispatch(fetchEvents({ page: 1, limit: 20 }));
    } else {
      toast({
        title: 'Revert failed',
        description: extractErrorMessage(result.payload),
        variant: 'destructive',
      });
    }
  };

  if (isLoading && !events) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Currency history</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Currency history</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">Failed to load: {error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="currency-history-timeline">
        <CardHeader>
          <CardTitle>Currency history</CardTitle>
          <CardDescription>
            Past currency conversions. Revert any completed event to roll back
            your records to the previous currency.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!events || events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No currency conversions yet. Change your currency in the field above
              to see history here.
            </p>
          ) : (
            <div className="space-y-3">
              {events.map((ev) => {
                const config = statusConfig[ev.status as keyof typeof statusConfig] || statusConfig.completed;
                const StatusIcon = config.icon;
                const isCompleted = ev.status === 'completed';
                return (
                  <div
                    key={ev._id}
                    className="flex items-start gap-3 px-4 py-3 rounded-lg border"
                    data-testid="currency-history-entry"
                  >
                    <StatusIcon className={`h-4 w-4 mt-1 ${config.color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {ev.fromCurrency} <ArrowRight className="inline h-3 w-3" /> {ev.toCurrency}
                        </span>
                        <Badge variant={isCompleted ? 'default' : 'secondary'} className="text-xs">
                          {config.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(ev.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {ev.recordsAffected} records · rate {ev.fxRate} · {ev.fxRateProvider}
                      </p>
                    </div>
                    {isCompleted && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRollbackTarget({
                          eventId: ev._id,
                          fromCurrency: ev.fromCurrency,
                          toCurrency: ev.toCurrency,
                        })}
                        data-testid="currency-history-revert"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Revert
                      </Button>
                    )}
                  </div>
                );
              })}
              {eventsPagination && eventsPagination.page < eventsPagination.totalPages && (
                <Button variant="outline" size="sm" className="w-full" onClick={handleLoadMore}>
                  Load more
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rollbackTarget} onOpenChange={(open) => !open && setRollbackTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revert currency conversion</DialogTitle>
            <DialogDescription>
              This will roll back the conversion from {rollbackTarget?.fromCurrency} to{' '}
              {rollbackTarget?.toCurrency}. Your records will be reset to the previous
              currency. This action writes a new audit log entry.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="rollback-reason">Reason for revert (required, min 5 chars)</Label>
              <Textarea
                id="rollback-reason"
                value={rollbackReason}
                onChange={(e) => setRollbackReason(e.target.value)}
                placeholder="e.g. Picked wrong currency by mistake"
                rows={3}
                data-testid="rollback-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleRollback}
              disabled={rollbackReason.trim().length < 5 || rollbackInProgress}
              data-testid="rollback-confirm"
            >
              {rollbackInProgress ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Reverting...
                </>
              ) : (
                'Confirm revert'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CurrencyHistoryTimeline;